import { useEffect, useState, type FC } from 'react';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { FeatureVersionCard } from './FeatureVersionCard';
import { FeatureInvitationModal } from './FeatureInvitationModal';
import { MENU_FEATURE_KEY } from '@utils/featureRollout/constants';
import { dismissInvitation, isInvitationDismissed } from '@utils/featureRollout/invitationPrefs';

// Conector del framework de rollout para `menu-v2`: cablea el hook + la identidad root
// con la card reutilizable y el modal contextual de primera vez. Se monta en el shell del
// menú (sobre la página resuelta).
//
// La card (banner discreto) cubre el estado persistente: invitación en control / regreso
// en treatment / auto-oculta en GA. El modal contextual REFUERZA la visibilidad la PRIMERA
// vez que un comercio elegible entra al menú (control + experimento activo, aún no aceptó),
// con "no volver a preguntar" persistido por comercio. Es no-bloqueante (se puede cerrar).
//
// El switch lo dispara el root; no-root recibe aviso suave (card y modal lo manejan).

const MenuVersionInvitation: FC = () => {
  const { variant, canRevert, gaForced, isEligibleInvitation, switchTo, revert, markInvitationShown } =
    useFeatureVersion(MENU_FEATURE_KEY);
  const { isRoot } = useSubscription();

  // El modal contextual se abre una vez por montaje del shell si el comercio es elegible y
  // no marcó antes "no volver a preguntar". Decisión SÍNCRONA (localStorage) → sin parpadeo.
  const [showModal, setShowModal] = useState(() => isEligibleInvitation && !isInvitationDismissed(MENU_FEATURE_KEY));

  // El flag de elegibilidad puede llegar después de hidratar (control → resolución backend).
  // Si pasa a elegible y no estaba descartado, abrimos el modal una sola vez.
  const openedRef = useState(() => ({ done: false }))[0];
  useEffect(() => {
    if (openedRef.done) return;
    if (isEligibleInvitation && !isInvitationDismissed(MENU_FEATURE_KEY)) {
      openedRef.done = true;
      setShowModal(true);
    }
  }, [isEligibleInvitation, openedRef]);

  const closeModal = () => setShowModal(false);
  const neverAsk = () => {
    dismissInvitation(MENU_FEATURE_KEY);
    setShowModal(false);
  };
  const acceptFromModal = () => {
    switchTo('treatment', 'menu_modal');
    setShowModal(false);
  };

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <FeatureVersionCard
          variant={variant}
          canRevert={canRevert}
          gaForced={gaForced}
          isRoot={Boolean(isRoot)}
          onTry={() => switchTo('treatment', 'menu_shell')}
          onRevert={() => revert('menu_shell')}
          onShown={() => markInvitationShown('menu_shell')}
        />
      </div>
      {showModal && (
        <FeatureInvitationModal
          isRoot={Boolean(isRoot)}
          onTry={acceptFromModal}
          onDismiss={closeModal}
          onNeverAsk={neverAsk}
          onShown={() => markInvitationShown('menu_modal')}
        />
      )}
    </>
  );
};

export default MenuVersionInvitation;
